// Valid IANA time-zone identifiers, used to constrain time-zone <select>s so an
// admin can never hand-type an invalid zone. An invalid zone (e.g. the legacy
// "United Arab Emirates/Dubai" that a free-text field once let through) throws in
// Intl.DateTimeFormat downstream and silently breaks live-session time rendering
// on the site and app.

// Curated fallback for the rare runtime whose Intl build predates
// Intl.supportedValuesOf. Every entry is a real IANA zone.
const CURATED_FALLBACK = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Sao_Paulo',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Moscow',
    'Africa/Cairo',
    'Asia/Dubai',
    'Asia/Karachi',
    'Asia/Kolkata',
    'Asia/Dhaka',
    'Asia/Bangkok',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
];

/** The full IANA time-zone list from the runtime, or a curated fallback. */
export const IANA_TIME_ZONES: string[] = (() => {
    try {
        // `supportedValuesOf` may be absent in the TS lib types on older targets.
        const fn = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
        const zones = fn?.('timeZone');
        if (zones && zones.length > 0) return zones;
    } catch {
        // fall through to the curated fallback
    }
    return CURATED_FALLBACK;
})();

/**
 * True iff `tz` is a zone the runtime's Intl/ICU recognises. The RangeError that
 * Intl.DateTimeFormat throws on an unknown zone is the canonical "is this a real
 * IANA zone" test — more permissive than IANA_TIME_ZONES membership because it
 * also accepts aliases (e.g. 'US/Eastern') the picker doesn't list.
 */
export const isValidIanaTimeZone = (tz: string | undefined | null): boolean => {
    if (!tz) return false;
    try {
        Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    } catch {
        return false;
    }
};
